import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip
} from '@mui/material';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close, Transform as TransformIcon } from '@mui/icons-material';
import { partService } from '../services/partService';
import { useAuth } from '../hooks/useAuth';
import NewPartModal from '../components/NewPartModal';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';

const emptyPartForm = () => ({
  part_number: '',
  description: '',
  unit_cost: '',
  unit_price: '',
  quantity_on_hand: '',
  minimum_quantity: '1',
  supplier: '',
  category: '',
  is_active: true
});

const rowToForm = (row) => ({
  part_number: row.part_number ?? '',
  description: row.part_description ?? row.description ?? '',
  unit_cost: row.unit_cost != null ? String(row.unit_cost) : '',
  unit_price: row.unit_price != null ? String(row.unit_price) : '',
  quantity_on_hand: row.quantity_on_hand != null ? String(row.quantity_on_hand) : '',
  minimum_quantity: row.minimum_quantity != null ? String(row.minimum_quantity) : '1',
  supplier: row.supplier ?? '',
  category: row.category ?? '',
  is_active: row.is_active !== false
});

/** Words `inactive` or `deactivated` in the search box limit results to deactivated parts; other words are used as text search (description, supplier, part #). */
function parsePartsSearchQuery(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return { textSearch: '', inactiveOnly: false };
  const tokens = trimmed.split(/\s+/);
  let inactiveOnly = false;
  const kept = [];
  for (const t of tokens) {
    const low = t.toLowerCase();
    if (low === 'inactive' || low === 'deactivated') {
      inactiveOnly = true;
    } else {
      kept.push(t);
    }
  }
  return { textSearch: kept.join(' ').trim(), inactiveOnly };
}

const Parts = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const DEFAULT_COL_WIDTHS = {
    partNo: 130,
    desc: 240,
    status: 88,
    qty: 90,
    min: 90,
    unitPrice: 120,
    quotes: 80,
    orders: 80,
    invoices: 80,
    slips: 90,
    category: 120,
    supplier: 150
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactiveParts, setShowInactiveParts] = useState(() => {
    try {
      return window.localStorage.getItem('wm_parts_show_inactive') === 'true';
    } catch {
      return false;
    }
  });
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyPartForm());
  const [saving, setSaving] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertServiceCode, setConvertServiceCode] = useState('');
  const [convertSaving, setConvertSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_parts_col_widths',
    minWidths: {
      partNo: 100,
      desc: 140,
      status: 72,
      qty: 70,
      min: 70,
      unitPrice: 100,
      quotes: 70,
      orders: 70,
      invoices: 70,
      slips: 80,
      category: 100,
      supplier: 120
    }
  });

  const loadParts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const { textSearch, inactiveOnly } = parsePartsSearchQuery(searchTerm);
      const params = {
        search: textSearch || undefined,
        limit: 200,
        inactive_only: inactiveOnly ? 'true' : undefined,
        activeOnly: inactiveOnly ? 'false' : showInactiveParts ? 'false' : 'true'
      };
      const res = await partService.getAll(params);
      const list = res.data ?? [];
      setParts(list);
      setSelectedPart(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading parts:', err);
      setError(err.response?.data?.message || 'Failed to load parts');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showInactiveParts]);

  useEffect(() => {
    loadParts();
  }, [loadParts]);

  const handleSearch = () => {
    loadParts();
  };

  const openNewDialog = () => {
    setNewDialogOpen(true);
    setError('');
  };

  const openEditDialog = (row) => {
    if (!row) return;
    setFormData(rowToForm(row));
    setSelectedPart(row);
    setEditDialogOpen(true);
    setError('');
  };

  const handleUpdatePart = async () => {
    if (!selectedPart?.id) return;
    const { description } = formData;
    if (!description?.trim()) {
      setError('Description is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        ...formData,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
        quantity_on_hand: formData.quantity_on_hand != null && formData.quantity_on_hand !== '' ? parseInt(formData.quantity_on_hand, 10) : null,
        minimum_quantity: formData.minimum_quantity != null && formData.minimum_quantity !== '' ? parseInt(formData.minimum_quantity, 10) : null
      };
      await partService.update(selectedPart.id, payload);
      setEditDialogOpen(false);
      setSelectedPart(null);
      loadParts();
    } catch (err) {
      console.error('Error updating part:', err);
      setError(err.response?.data?.message || 'Failed to update part');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePart = async () => {
    if (!selectedPart?.id) return;
    try {
      setSaving(true);
      setError('');
      await partService.delete(selectedPart.id);
      setDeleteConfirmOpen(false);
      setSelectedPart(null);
      loadParts();
    } catch (err) {
      console.error('Error deleting part:', err);
      setError(err.response?.data?.message || 'Failed to deactivate part');
    } finally {
      setSaving(false);
    }
  };

  const openConvertDialog = () => {
    if (!selectedPart) return;
    setConvertServiceCode(
      selectedPart.part_number != null && String(selectedPart.part_number).trim() !== ''
        ? String(selectedPart.part_number).trim()
        : ''
    );
    setConvertDialogOpen(true);
    setError('');
  };

  const handleConvertToService = async () => {
    if (!selectedPart?.id) return;
    try {
      setConvertSaving(true);
      setError('');
      const body = {};
      const t = convertServiceCode.trim();
      if (t) body.service_code = t;
      const res = await partService.convertToService(selectedPart.id, body);
      const svc = res.data?.service;
      const u = res.data?.updated || {};
      setConvertDialogOpen(false);
      setSuccessMessage(
        `Created service "${effectiveServiceCode(svc)}" (ID ${svc?.id}). Updated quote lines: ${u.quote_items}, order lines: ${u.work_order_items}, invoice lines: ${u.customer_invoice_items}, packing slip rows: ${u.packing_slip_items}. The part was deactivated.`
      );
      await loadParts();
    } catch (err) {
      console.error('Convert part to service:', err);
      const d = err.response?.data;
      let msg = d?.message || err.message || 'Failed to convert part';
      if (d?.blocking) {
        const bits = Object.entries(d.blocking)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `${k}: ${v}`);
        if (bits.length) msg += ` (${'; '.join(bits)})`;
      }
      setError(msg);
    } finally {
      setConvertSaving(false);
    }
  };

  const formFields = (
    <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Part Number" value={formData.part_number} onChange={(e) => setFormData((f) => ({ ...f, part_number: e.target.value }))} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Description" multiline rows={2} value={formData.description} onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))} required />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CurrencyTextField fullWidth size="small" label="Unit Cost" value={formData.unit_cost} onChange={(e) => setFormData((f) => ({ ...f, unit_cost: e.target.value }))} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CurrencyTextField fullWidth size="small" label="Unit Price" value={formData.unit_price} onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Qty on Hand" type="number" value={formData.quantity_on_hand} onChange={(e) => setFormData((f) => ({ ...f, quantity_on_hand: e.target.value }))} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Minimum Qty" type="number" value={formData.minimum_quantity} onChange={(e) => setFormData((f) => ({ ...f, minimum_quantity: e.target.value }))} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Supplier" value={formData.supplier} onChange={(e) => setFormData((f) => ({ ...f, supplier: e.target.value }))} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Category" value={formData.category} onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))} />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Parts
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedPart && openEditDialog(selectedPart)} disabled={!selectedPart}>
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => selectedPart && setDeleteConfirmOpen(true)}
            disabled={!selectedPart}
          >
            Deactivate
          </Button>
          {isAdmin() && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<TransformIcon />}
              onClick={() => selectedPart && openConvertDialog()}
              disabled={!selectedPart}
            >
              Change to Service
            </Button>
          )}
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNewDialog}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Selected Part Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Part #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedPart?.part_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            {selectedPart?.is_active === false ? (
              <Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', mt: 0.25 }} />
            ) : (
              <Typography variant="body2">Active</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Qty</Typography>
            <Typography variant="body2">
              {selectedPart?.quantity_on_hand != null ? selectedPart.quantity_on_hand : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Min</Typography>
            <Typography variant="body2">
              {selectedPart?.minimum_quantity != null ? selectedPart.minimum_quantity : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Unit Price</Typography>
            <Typography variant="body2">
              {selectedPart ? formatCurrency(selectedPart.unit_price) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Category</Typography>
            <Typography variant="body2">
              {selectedPart?.category ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Supplier</Typography>
            <Typography variant="body2">
              {selectedPart?.supplier ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quotes</Typography>
            <Typography variant="body2">
              {(selectedPart?.quote_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/quotes?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.quote_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Orders</Typography>
            <Typography variant="body2">
              {(selectedPart?.order_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.order_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoices</Typography>
            <Typography variant="body2">
              {(selectedPart?.invoice_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customer-invoices?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.invoice_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slips</Typography>
            <Typography variant="body2">
              {(selectedPart?.packing_slip_references ?? 0) > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/packing-slips?part_id=${selectedPart.id}`)}
                >
                  {selectedPart.packing_slip_references}
                </Button>
              ) : (
                0
              )}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Description</Typography>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedPart?.part_description ?? selectedPart?.description ?? '—'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedPart(null)}
            disabled={!selectedPart}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search description, supplier, part # — add inactive or deactivated to list only deactivated parts"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
          <FormControlLabel
            control={(
              <Checkbox
                checked={showInactiveParts}
                onChange={(e) => {
                  const v = e.target.checked;
                  setShowInactiveParts(v);
                  try {
                    window.localStorage.setItem('wm_parts_show_inactive', v ? 'true' : 'false');
                  } catch {
                    /* ignore */
                  }
                }}
                size="small"
              />
            )}
            label={<Typography variant="body2">Show deactivated parts</Typography>}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.partNo} onResizeStart={(e) => startResize('partNo', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Part #</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.desc} onResizeStart={(e) => startResize('desc', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Part Description</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Status</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.qty} onResizeStart={(e) => startResize('qty', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Qty</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.min} onResizeStart={(e) => startResize('min', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Min</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.unitPrice} onResizeStart={(e) => startResize('unitPrice', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Unit Price</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.quotes} onResizeStart={(e) => startResize('quotes', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Quotes</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.orders} onResizeStart={(e) => startResize('orders', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Orders</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.invoices} onResizeStart={(e) => startResize('invoices', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Invoices</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.slips} onResizeStart={(e) => startResize('slips', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Packing Slips</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.category} onResizeStart={(e) => startResize('category', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Category</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.supplier} onResizeStart={(e) => startResize('supplier', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Supplier</Typography></ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : parts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No parts found. Use New to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              parts.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedPart?.id === row.id}
                  onClick={() => setSelectedPart(row)}
                  onDoubleClick={() => openEditDialog(row)}
                  sx={{
                    cursor: 'pointer',
                    ...(row.is_active === false ? { opacity: 0.88, bgcolor: 'action.hover' } : {})
                  }}
                >
                  <TableCell sx={{ width: colWidths.partNo }}>{row.part_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.desc, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.part_description ?? row.description ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.status }}>
                    {row.is_active === false ? (
                      <Chip label="Inactive" size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">Active</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.qty }}>{row.quantity_on_hand != null ? row.quantity_on_hand : '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.min }}>{row.minimum_quantity != null ? row.minimum_quantity : '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.unitPrice }}>{formatCurrency(row.unit_price)}</TableCell>
                  <TableCell sx={{ width: colWidths.quotes }}>
                    {(row.quote_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/quotes?part_id=${row.id}`);
                        }}
                      >
                        {row.quote_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.orders }}>
                    {(row.order_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/work-orders?part_id=${row.id}`);
                        }}
                      >
                        {row.order_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.invoices }}>
                    {(row.invoice_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customer-invoices?part_id=${row.id}`);
                        }}
                      >
                        {row.invoice_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.slips }}>
                    {(row.packing_slip_references ?? 0) > 0 ? (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/packing-slips?part_id=${row.id}`);
                        }}
                      >
                        {row.packing_slip_references}
                      </Button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.category }}>{row.category ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.supplier }}>{row.supplier ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <NewPartModal
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreated={() => loadParts()}
      />

      <Dialog open={editDialogOpen} onClose={() => !saving && (setEditDialogOpen(false), setSelectedPart(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Part</DialogTitle>
        <DialogContent>{formFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedPart(null); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdatePart} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Deactivate Part</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate part{' '}
            <strong>{selectedPart?.part_number ?? selectedPart?.part_description ?? selectedPart?.description ?? selectedPart?.id}</strong>
            . This will only mark it inactive for inventory selection; existing Quotes, Orders, Invoices, and Packing Slips will remain unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePart} disabled={saving}>
            {saving ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={convertDialogOpen}
        onClose={() => !convertSaving && setConvertDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change part to service</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This creates a new service from the selected part, rewrites matching quote, order, and invoice lines to that service, clears part links on packing slip rows, and deactivates the part.
            Blocked if the part is still on purchase orders, receivers, product BOM, or inventory history.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Part:{' '}
            <strong>
              {selectedPart?.part_number ?? selectedPart?.id} — {selectedPart?.part_description ?? selectedPart?.description ?? '—'}
            </strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            References — Quotes: {selectedPart?.quote_references ?? 0}, Orders: {selectedPart?.order_references ?? 0}, Invoices:{' '}
            {selectedPart?.invoice_references ?? 0}, Packing slips: {selectedPart?.packing_slip_references ?? 0}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Service code (optional)"
            helperText="Leave blank to use the part number, or enter letters, numbers, and dashes only."
            value={convertServiceCode}
            onChange={(e) => setConvertServiceCode(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={convertSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConvertToService} disabled={convertSaving}>
            {convertSaving ? 'Converting…' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Parts;
